import { Button, Checkbox, Form, Input } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import RegistrationHeader from '../../components/Header/RegistrationHeader';
import useResetPassword from './useResetPassword';

import styles from './ResetPassword.module.scss';
import { Role } from './ResetPassword.model';
import Icon from '../../components/Icon/Icon';

import { WORDPRESS_DOMAIN } from '../../Helpers/EnvVars';

const ResetPassword = () => {
	const {
		intl,
		navigateTo,
		onPasswordChange,
		onSubmit,
		state: {
			invalidToken,
			page,
			passwordStrength,
			role
		},
	} = useResetPassword();

	const defaultValues = {
		acceptedCoc: false,
		acceptedTac: false,
		password: '',
		passwordConfirm: '',
	};

	const { control, handleSubmit, formState: { errors }, watch } = useForm({ defaultValues });

	const watchPassword = watch("password");

	const renderInvalidTokenMessage = () => {
		return <>
			<div className={styles.iconWrapper}>
				<Icon name="hourglass-end" width="80px" height="80px" />
			</div>
			<h1>
				{page?.subtitle}
			</h1>
			<p>
				{page?.info}
			</p>
		</>
	}

	const renderForm = () => {
		return <>
			<h1>{page?.subtitle}</h1>
			<Form onFinish={handleSubmit(onSubmit)}>
				<div className={styles.fieldsContainer}>

					<div className={`${styles.registrationInput} ${styles.fullWidth} ${errors.password ? styles.error : ''}`}>
						<label><FormattedMessage id="formLabels.password" /></label>
						<Controller
							name="password"
							control={control}
							rules={{
								min: 8,
								pattern: /^(?=.*[a-z])(?=.*[0-9]).{8,}$/,
								required: intl.formatMessage({ id: 'formErrors.password.required' }),
							}}
							render={({ field }) => (
								<div className={styles.passwordWrapper}>
									<Input.Password
										{...field}
										bordered={false}
										className={errors.password && 'error'}
										visibilityToggle={false}
										onChange={(event) => {
											field.onChange(event);
											onPasswordChange(event);
										}}
									/>
									<span className={styles[passwordStrength]}><FormattedMessage id={`formErrors.password.${passwordStrength}`} /></span>
								</div>
							)}
						/>
						{errors.password?.type === "required" && <div className={styles.errorMessage}>{errors.password.message}</div>}
						{errors.password?.type === "pattern" && <div className={styles.errorMessage}><FormattedMessage id="formErrors.password.pattern" /></div>}
					</div>

					<div className={`${styles.registrationInput} ${styles.fullWidth} ${errors.passwordConfirm ? styles.error : ''}`}>
						<label><FormattedMessage id="formLabels.passwordRetype" /></label>
						<Controller
							name="passwordConfirm"
							control={control}
							rules={{
								required: intl.formatMessage({ id: 'formErrors.password.required' }),
								validate: value => value === watchPassword || intl.formatMessage({ id: 'formErrors.password.mismatch' })
							}}
							render={({ field }) => (
								<Input.Password
									{...field}
									className={errors.passwordConfirm && 'error'}
									bordered={false}
									visibilityToggle={false}
								/>
							)}
						/>
						{errors.passwordConfirm && <div className={styles.errorMessage}>{errors.passwordConfirm.message}</div>}
					</div>

					{page?.showConsentCheckboxes && <div className={`${styles.checkbox} ${errors.acceptedTac ? styles.error : ''}`}>
						<Controller
							name="acceptedTac"
							control={control}
							rules={{
								required: true,
							}}
							render={({ field }) => (
								<Checkbox
									{...field}
									checked={field.value}
								>
									<FormattedMessage id="formLabels.consent" />&nbsp;
									<a href={`${WORDPRESS_DOMAIN}/terms-and-conditions`} target="_blank" rel="noreferrer"><FormattedMessage id="formLabels.terms" /></a>
								</Checkbox>
							)}
						/>
					</div>}
					{page?.showConsentCheckboxes && role === Role.Mentor && <div className={`${styles.checkbox} ${errors.acceptedCoc ? styles.error : ''}`}>
						<Controller
							name="acceptedCoc"
							control={control}
							rules={{
								required: true,
							}}
							render={({ field }) => (
								<Checkbox
									{...field}
									checked={field.value}
								>
									<FormattedMessage id="formLabels.consent" />&nbsp;
									<a href={`${WORDPRESS_DOMAIN}/code-of-conduct`} target="_blank" rel="noreferrer"><FormattedMessage id="formLabels.consentCodeOfConduct" /></a>
								</Checkbox>
							)}
						/>
					</div>}

				</div>

				<div className={styles.buttonsContainer}>
					<Button
						htmlType="submit"
						size="large"
						type="primary"
					>
						<FormattedMessage id="buttons.setPassword" />
					</Button>
				</div>
			</Form>
		</>
	};

	return (
		<div className={styles.pageWrapper}>
			<div className={styles.pageContainer}>

				<div className={styles.pageContent}>

					<RegistrationHeader
						actionLabel={intl.formatMessage({ id: 'buttons.login' })}
						onAction={navigateTo.bind(null, '/')}
					/>

					<div className={`${styles.content} ${invalidToken && styles.invalidTokenWrapper}`}>
						<h3>{page?.title}</h3>
						{!invalidToken ?
							renderForm() :
							renderInvalidTokenMessage()}
					</div>

				</div>

				<div className={styles.pageSider}></div>
			</div>

		</div>
	);
};

export default ResetPassword;